import React, { useEffect } from 'react';
import ApexCharts from 'apexcharts';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

interface RentalTrendsChartProps {
  data: {
    newRentals: number[];
    reRentals: number[];
    newRentalLinks: string[];
    reRentalLinks: string[];
    months: string[];
  };
}

export const RentalTrendsChart: React.FC<RentalTrendsChartProps> = ({ data }) => {
  const { t } = useTranslation();

  useEffect(() => {
    const options = {
      series: [
        {
          name: 'New rentals',
          data: data.newRentals,
          color: '#1B998B',
        },
        {
          name: 'Re-rentals',
          data: data.reRentals,
          color: '#3C6895',
        },
      ],
      chart: {
        height: 350,
        type: 'bar',
        events: {
          dataPointSelection(event, chartContext, config) {
            const { seriesIndex, dataPointIndex } = config;
            const series = seriesIndex === 0 ? data.newRentalLinks : data.reRentalLinks;

            window.open(series[dataPointIndex], '_blank');
          },
        },
        toolbar: {
          show: false,
        },
        fontFamily: 'inherit',
      },
      plotOptions: {
        bar: {
          horizontal: false,
          columnWidth: 31,
          borderRadiusApplication: 'end',
          dataLabels: {
            position: 'top',
          },
          distributed: false,
          rangeBarOverlap: true,
          barHeight: '100%',
        },
      },
      dataLabels: {
        enabled: false,
      },
      stroke: {
        width: 0,
        colors: ['#000000'],
      },
      xaxis: {
        categories: data.months,
        type: 'category',
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
        labels: {
          style: {
            colors: '#495057',
            fontSize: '14px',
            fontWeight: 500,
          },
        },
      },
      yaxis: {
        labels: {
          formatter: (val: number) => Math.round(val).toString(),
          style: {
            colors: '#495057',
            fontSize: '14px',
            fontWeight: 500,
          },
        },
      },
      states: {
        active: {
          allowMultipleDataPointsSelection: false,
          filter: {
            type: 'none',
          },
        },
        hover: {
          filter: {
            type: 'none',
          },
        },
      },
      tooltip: {
        shared: true,
        intersect: false,
        custom({ series, dataPointIndex }: any) {
          const newRentals = series[0][dataPointIndex];
          const reRentals = series[1][dataPointIndex];
          const month = data.months[dataPointIndex];

          return `
              <div style="
                padding: 8px;
                background: white;
                border-radius: 16px;
                font-family: 'sofia-pro', sans-serif;
                min-width: 180px;
                border: none;
              ">
                <div style="
                  display: flex;
                  align-items: center;
                  margin-bottom: 12px;
                  font-size: 14px;
                  color: #212529;
                  font-family: 'sofia-pro', sans-serif;
                  font-weight: 700;
                ">
                  <span style="
                    width: 12px;
                    height: 12px;
                    background: #1B998B;
                    border-radius: 50%;
                    display: inline-block;
                    margin-right: 12px;
                  "></span>
                  New rentals: ${newRentals}
                </div>
                <div style="
                  display: flex;
                  align-items: center;
                  font-size: 14px;
                  color: #212529;
                  font-family: 'sofia-pro', sans-serif;
                  font-weight: 700;
                ">
                  <span style="
                    width: 12px;
                    height: 12px;
                    background: #3C6895;
                    border-radius: 50%;
                    display: inline-block;
                    margin-right: 12px;
                  "></span>
                  Re-rentals: ${reRentals}
                </div>
              </div>
          `;
        },
      },
      legend: {
        show: false,
      },
    };

    const chart = new ApexCharts(document.querySelector('#rental_trends_chart'), options);
    chart.render();

    return () => {
      chart.destroy();
    };
  }, [data]);

  return (
    <Box
      className="w-full rounded-[16px] bg-white p-4 shadow-small"
      sx={{
        '& .apexcharts-tooltip': {
          border: 'none',
          width: '230px',
          height: '98px',
          borderRadius: '16px',
          padding: '15px',
          background: 'white',
          fontFamily: 'sofia-pro',
          gap: '8px',
        },
        '& .apexcharts-theme-light': {
          boxShadow: '0px 8px 24px 0px #71829126',
        },
        '& .apexcharts-tooltip.apexcharts-theme-light': {
          border: 'none',
          background: '#FFFFFF',
        },
        '& .apexcharts-bar-area': {
          transition: 'all 0.15s ease',
        },
        '& .apexcharts-series .apexcharts-bar-area': {
          opacity: '1',
        },
        '& .apexcharts-series:has(~ .apexcharts-series .apexcharts-bar-area:hover) .apexcharts-bar-area, & .apexcharts-series:has(.apexcharts-bar-area:hover) ~ .apexcharts-series .apexcharts-bar-area, & .apexcharts-series:has(.apexcharts-bar-area:hover) .apexcharts-bar-area:not(:hover)':
          {
            opacity: '0.3',
          },
        '& .apexcharts-bar-area:hover': {
          opacity: '1 !important',
          stroke: '#000000',
          strokeWidth: '2px',
          cursor: 'pointer',
        },
      }}
    >
      <Typography className="mb-3 font-Vollkorn text-lead1 font-bold leading-[27px] text-[#111827]">
        {t('js.features.admin.stats.dashboard.rental_trends_chart.title')}
      </Typography>
      <div id="rental_trends_chart" />

      {/* Legend below the chart */}
      <Box className="mt-2 flex justify-center gap-4">
        {[
          { label: 'New rentals', color: '#1FB2A2' },
          { label: 'Re-rentals', color: '#3C6895' },
        ].map((item) => (
          <Box key={item.label} className="flex items-center">
            <Box
              sx={{ width: 12, height: 12 }}
              className="mr-1 rounded-full"
              style={{ backgroundColor: item.color }}
            />
            <Typography
              variant="lead1"
              className="vacancy-maintenance-overview text-small font-medium text-N700"
              sx={{
                '&.pdf-export': {
                  marginTop: '-10px',
                },
              }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};
